/**
 * EditorStepFactory — Creates default steps and provides step type metadata.
 */
import type { CinematicStep } from '../CinematicTypes';
import type { StepTypeMeta } from './EditorTypes';

let idCounter = 0;

/** Generate a short unique step ID */
export function generateStepId(): string {
  idCounter++;
  return `step_${Date.now().toString(36)}_${idCounter}`;
}

/** Display metadata for each step type (used by timeline cards and add menu) */
export const STEP_TYPE_METAS: StepTypeMeta[] = [
  { type: 'dialogue',   label: 'Dialogue',   color: 0xffcc00, icon: '💬' },
  { type: 'cameraPan',  label: 'Camera Pan', color: 0x44aaff, icon: '🎥' },
  { type: 'fakeClick',  label: 'Fake Click', color: 0xff8844, icon: '👆' },
  { type: 'action',     label: 'Action',     color: 0x44ff88, icon: '⚡' },
  { type: 'wait',       label: 'Wait',       color: 0x888899, icon: '⏳' },
  { type: 'recap',      label: 'Recap',      color: 0xaa88ff, icon: '📋' },
  { type: 'spotlight',  label: 'Spotlight',  color: 0xff44cc, icon: '🔦' },
];

/** Get metadata for a step type */
export function getStepMeta(type: string): StepTypeMeta {
  return STEP_TYPE_METAS.find(m => m.type === type) ?? STEP_TYPE_METAS[4]; // fallback: wait
}

/**
 * Inspect previous dialogue steps and set smart defaults for a new dialogue:
 * - Alternates position (bottom ↔ top) relative to the last dialogue
 * - Offsets bubblePos if the previous dialogue had a custom one
 */
export function applySmartDialogueDefaults(
  step: CinematicStep,
  allSteps: CinematicStep[],
): void {
  if (step.type !== 'dialogue') return;

  // Find the last dialogue step before this one
  const dialogues = allSteps.filter(
    (s): s is Extract<CinematicStep, { type: 'dialogue' }> =>
      s.type === 'dialogue' && s.id !== step.id,
  );
  const prev = dialogues.length > 0 ? dialogues[dialogues.length - 1] : null;
  if (!prev) return;

  // Alternate position
  const prevPos = prev.position ?? 'bottom';
  step.position = prevPos === 'bottom' ? 'top' : prevPos === 'top' ? 'bottom' : 'top';

  // If previous had a custom bubblePos, offset vertically for the new one
  if (prev.bubblePos) {
    const flipY = step.position === 'top' ? 40 : 300;
    step.bubblePos = { x: prev.bubblePos.x, y: flipY };
  }
}

/** Create a new step with sensible defaults for the given type */
export function createDefaultStep(type: CinematicStep['type']): CinematicStep {
  const id = generateStepId();

  switch (type) {
    case 'dialogue':
      return {
        id, type: 'dialogue',
        speaker: 'cinematic.speaker.operator',
        textKey: 'NEW_DIALOGUE',
        position: 'bottom',
        emotion: 'calm',
        autoDismissMs: 3000,
        bubbleTemplate: 'trueBD',
      };
    case 'cameraPan':
      return {
        id, type: 'cameraPan',
        target: { x: 600, y: 400 },
        zoom: 1.5,
        duration: 800,
      };
    case 'fakeClick':
      return {
        id, type: 'fakeClick',
        target: { x: 600, y: 400 },
        holdMs: 500,
      };
    case 'action':
      return {
        id, type: 'action',
        action: 'spawnEnemies',
        params: { count: 3, type: 'grunt', grade: 'normal', pathIndex: 0 },
      };
    case 'wait':
      return {
        id, type: 'wait',
        timeoutMs: 2000,
      };
    case 'recap':
      return {
        id, type: 'recap',
        titleKey: 'NEW_RECAP_TITLE',
        items: [{ textKey: 'NEW_RECAP_ITEM' }],
        confirmKey: 'OK',
      };
    case 'spotlight':
      return {
        id, type: 'spotlight',
        target: 'placementTile',
        dimAlpha: 0.55,
        highlightColor: 0xffcc00,
        pulse: true,
        advanceOn: 'click',
        restrictInput: true,
      };
  }
}
